import {
	addTimeToDate,
	Button,
	isSameTime,
	Row,
	stringToLocalDate,
	toLocalDateString,
	Tooltip,
} from "@hex-insights/core";
import {
	DateTimeField,
	FormState,
	FormTooltipButton,
	FormType,
	StandingForm,
	useFormState,
	ValidationDisplayPolicy,
} from "@hex-insights/forms";
import { ButtonSwitch } from "../../../../../../Components/ButtonSwitch";
import { Icon } from "../../../../../../Components/Icon";
import styles from "./styles.module.css";

export type DateFormValues = {
	date: string | null;
};

export function useDateFormState(initialDate = toLocalDateString(new Date())) {
	const dateFormState = useFormState<DateFormValues>({
		initialFormValues: { date: initialDate },
		formType: FormType.Standing,
		validationDisplayPolicy: ValidationDisplayPolicy.none,
	});
	const date = dateFormState.formValues.date ?? initialDate;

	return { dateFormState, date };
}

export type DateFormTooltipButtonProps = {
	formState: FormState<DateFormValues>;
	date: string;
};

export function DateFormTooltipButton({ formState, date }: DateFormTooltipButtonProps) {
	return (
		<FormTooltipButton formState={formState} side="bottom" autoFocus>
			{() => (
				<Tooltip isInteractable>
					<Tooltip.Body style={{ padding: "1rem" }}>
						<StandingForm>
							<DateTimeField formState={formState} name="date" noHeader />
						</StandingForm>
					</Tooltip.Body>
				</Tooltip>
			)}
			{({ onClick }) => (
				<Button variant="tertiary" size="small" onClick={onClick}>
					<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
						<span className={styles["modal__report__date"]}>
							{stringToLocalDate(date, "day").toLocaleString(undefined, {
								year: "numeric",
								month: "short",
								day: "numeric",
								weekday: "long",
							})}
						</span>
						<Icon.CaretDown size={12} />
					</Row>
				</Button>
			)}
		</FormTooltipButton>
	);
}

export type DateRangeFormValues = {
	startDate: string | null;
	endDate: string | null;
};

export function useDateRangeFormState(initialDate = toLocalDateString(new Date())) {
	const initialStartDate = toLocalDateString(addTimeToDate(stringToLocalDate(initialDate, "day"), [-6, "days"]));

	const dateFormState = useFormState<DateRangeFormValues>({
		initialFormValues: {
			startDate: initialStartDate,
			endDate: initialDate,
		},
		formType: FormType.Standing,
		validationDisplayPolicy: ValidationDisplayPolicy.none,
	});
	const startDate = dateFormState.formValues.startDate ?? initialStartDate;
	const endDate = dateFormState.formValues.endDate ?? initialDate;

	return { dateFormState, startDate, endDate };
}

export type DateRangeFormTooltipButtonProps = {
	formState: FormState<DateRangeFormValues>;
	startDate: string;
	endDate: string;
};

export function DateRangeFormTooltipButton({ formState, startDate, endDate }: DateRangeFormTooltipButtonProps) {
	return (
		<FormTooltipButton formState={formState} side="bottom" autoFocus>
			{() => (
				<Tooltip isInteractable className={styles["date-range-form-tooltip"]}>
					<Tooltip.Body style={{ padding: "1rem" }}>
						<StandingForm>
							<Row justify="spaced-start" withVerticalSpacing overflow="wrap">
								<DateTimeField formState={formState} name="startDate" noToolbar />
								<DateTimeField formState={formState} name="endDate" noToolbar />
							</Row>
						</StandingForm>
					</Tooltip.Body>
				</Tooltip>
			)}
			{({ onClick }) => (
				<Button variant="tertiary" size="small" onClick={onClick}>
					<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
						<span className={styles["modal__report__date"]}>
							{stringToLocalDate(startDate, "day").toLocaleString(undefined, {
								year: isSameTime(startDate, endDate, "year") ? undefined : "numeric",
								month: "short",
								day: "numeric",
								weekday: "long",
							})}{" "}
							-{" "}
							{stringToLocalDate(endDate, "day").toLocaleString(undefined, {
								year: "numeric",
								month: "short",
								day: "numeric",
								weekday: "long",
							})}
						</span>
						<Icon.CaretDown size={12} />
					</Row>
				</Button>
			)}
		</FormTooltipButton>
	);
}

export type TableChartButtonSwitchProps = {
	view: string;
	setView: (view: string) => void;
};

export function TableChartButtonSwitch({ view, setView }: TableChartButtonSwitchProps) {
	return (
		<ButtonSwitch value={view} setValue={setView}>
			<ButtonSwitch.Button value="table" size="small">
				<Icon.ListDashes size={14} className="block" />
			</ButtonSwitch.Button>
			<ButtonSwitch.Button value="chart" size="small">
				<Icon.ChartLine size={14} className="block" />
			</ButtonSwitch.Button>
		</ButtonSwitch>
	);
}
